import { createSlice } from '@reduxjs/toolkit';

import { logger } from 'src/utils/serverLogger';
import { requestProductDetails } from './thunks';
import type { ProductState } from './types';
import { adaptCatServiceAPIResponseForStore } from './utils/adaptCatServiceAPIResponseForStore';

export const initialState: Readonly<ProductState> = {
  actualCommissionRate: 0,
  allowsGiftMessage: false,
  allowsGiftReceipt: false,
  allowsGiftWrap: false,
  attributes: {},
  availability: 'available',
  campaignIds: [],
  available: true,
  backInStockAt: null,
  code: 0,
  deliveryByEvent: null,
  description: '',
  dimensions: '',
  freeDomesticDelivery: false,
  freeGiftWrap: false,
  images: [],
  inventoryStatus: 'in_stock',
  isNew: false,
  links: [],
  lowCommitmentProductFlag: null,
  lowCommitmentProductType: null,
  materials: '',
  breadcrumbCategoryId: null,
  numberOfReviews: 0,
  onSale: false,
  ingredients: null,
  otherInformation: '',
  partner: {
    id: 0,
    links: [],
    metaTagDescription: null,
    name: '',
    logo: '',
    shortcode: '',
    location: '',
  },
  preSalePrices: [],
  price: { amount: 0, currency: 'GBP' },
  prices: [{ amount: 0, currency: 'GBP' }],
  productRating: null,
  ratings: null,
  publicAttributes: null,
  purchasable: true,
  relatedCategories: [],
  salePercentage: 0,
  specialFeatures: {
    ecoFriendly: false,
    exclusive: false,
    madeInBritain: false,
    personalisable: false,
    requiresPhotoUpload: false,
  },
  title: '',
  variationsAvailable: '',
  video: null,
  shippingInfo: null,
  error: null,
  metaDescription: '',
  metaRobotsIndexable: true,
};

export const { actions, reducer } = createSlice({
  name: 'product',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(requestProductDetails.fulfilled, (state, { payload }) => {
        if (payload.redirectUrl) {
          state.redirectUrl = payload.redirectUrl;
          return;
        }

        // `options` are owned by the basket slice now (populated from the same
        // adapted payload via its own extraReducer), so strip them here.
        const { options: _options, ...productData } = payload.data
          ? adaptCatServiceAPIResponseForStore(payload.data)
          : ({} as ReturnType<typeof adaptCatServiceAPIResponseForStore>);

        return {
          ...initialState,
          ...productData,
          attributes: payload.attributes ?? {},
          campaignIds: payload.campaignIds ?? [],
          ratings: payload.ratings ?? null,
          publicAttributes: payload.publicAttributes ?? null,
        };
      })
      .addCase(requestProductDetails.rejected, (state, action) => {
        logger.error('Error requesting product data', action.error);

        state.error = action.error;
      });
  },
});
